import React, { useState } from 'react';
import { classes } from 'src/modules/shared/utils/styles.util';
import { useElementWidth } from 'src/modules/shared/hooks/use-element-width';

/** Props for the scaled website viewport. */
interface WebsiteScaledViewportProps {
  /** Width in CSS pixels at which the content renders before scaling. */
  baseWidth: number;
  /** Height in CSS pixels at which the content renders before scaling. */
  baseHeight: number;
  /** Content rendered at the base size, such as a site iframe. */
  children: React.ReactNode;
  /** Extra classes for the outer frame. */
  className?: string;
}

/**
 * Renders content at a fixed desktop size and scales it down to the frame
 * width, so a site preview keeps its desktop layout inside a small card.
 *
 * The content stays hidden until the frame is measured, which avoids a flash
 * at full size.
 *
 * @param props - Base size, content, and frame classes.
 * @returns A frame with the base size's aspect ratio and the scaled content.
 */
export const WebsiteScaledViewport: React.FC<WebsiteScaledViewportProps> = ({
  baseWidth,
  baseHeight,
  children,
  className,
}) => {
  const [frame, setFrame] = useState<HTMLDivElement | null>(null);
  const frameWidth = useElementWidth(frame);
  const scale = frameWidth > 0 ? frameWidth / baseWidth : 0;

  return (
    <div
      ref={setFrame}
      className={classes('relative w-full overflow-hidden', className)}
      style={{ aspectRatio: `${baseWidth} / ${baseHeight}` }}
    >
      <div
        className="absolute left-0 top-0 origin-top-left"
        style={{
          width: baseWidth,
          height: baseHeight,
          transform: `scale(${scale})`,
          visibility: scale > 0 ? 'visible' : 'hidden',
        }}
      >
        {children}
      </div>
    </div>
  );
};
